import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { extractErrorMessage } from '../utils/extractErrorMessage';
import Button from '../components/ui/Button';
import ActionRow from '../components/ui/ActionRow';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/pilot-ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/pilot-ui/alert-dialog';
import { Input } from '../components/pilot-ui/input';
import { Textarea } from '../components/pilot-ui/textarea';
import { Label } from '../components/pilot-ui/label';
import { Checkbox } from '../components/pilot-ui/checkbox';
import {
  FeatureFlag,
  FlagMutationResponse,
  FLAG_NAME_REGEX,
  parseUserIdsInput,
} from '../utils/featureFlags';
import {
  ApplyReceipt,
  CHART_FLAG_NAME,
  ChartAccessPlan,
  PlanDevice,
  buildAccessMap,
  deviceKey,
  receiptText,
  selectionSummary,
  userLabel,
  warningText,
} from '../utils/chartAccess';

// FS-496: Features admin section. CRUD over app feature flags; firebase-api
// owns the Firestore persistence and the batch rewrite of each affected app
// user's stored flag list.
const flagsUrl = () => `${config.firebaseApiUrl}/fb/feature-flags`;
// FS-548: the chart flag's second half — which devices each enabled user may
// open charts for. Proposed and saved server-side; reviewed here.
const chartAccessUrl = (flag: string) =>
  `${config.firebaseApiUrl}/fb/chart-access/${encodeURIComponent(flag)}`;

type ActiveModal =
  | { kind: 'none' }
  | { kind: 'create' }
  | { kind: 'edit'; flag: FeatureFlag }
  | { kind: 'delete'; flag: FeatureFlag }
  | { kind: 'access'; flag: FeatureFlag };

type Notice = { kind: 'success' | 'warning'; text: string };

const savedMessage = (verb: string, name: string, res: FlagMutationResponse): Notice => {
  const base =
    typeof res.users_updated === 'number'
      ? `Flag "${name}" ${verb} — ${res.users_updated} app user record${res.users_updated === 1 ? '' : 's'} updated.`
      : `Flag "${name}" ${verb}.`;
  const unknown = res.unknown_user_ids ?? [];
  if (unknown.length === 0) return { kind: 'success', text: base };
  // A typo'd ID silently leaves that user without the feature — make it loud.
  return {
    kind: 'warning',
    text: `${base} ${unknown.length} ID${unknown.length === 1 ? '' : 's'} matched no app user: ${unknown.join(', ')}.`,
  };
};

const FeaturesPage: React.FC = () => {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [modal, setModal] = useState<ActiveModal>({ kind: 'none' });
  const newFlagRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLButtonElement | null>(null);

  const loadFlags = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axiosInstance.get<FeatureFlag[]>(flagsUrl());
      setFlags([...res.data].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load feature flags'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFlags();
  }, [loadFlags]);

  const closeModal = () => setModal({ kind: 'none' });
  useEffect(() => {
    if (modal.kind === 'none' && returnFocusRef.current) {
      const target = returnFocusRef.current.isConnected ? returnFocusRef.current : newFlagRef.current;
      target?.focus();
      returnFocusRef.current = null;
    }
  }, [modal.kind]);

  const handleSaved = (notice: Notice, removed = false) => {
    setNotice(notice);
    if (removed) returnFocusRef.current = newFlagRef.current;
    closeModal();
    loadFlags();
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-card border border-border rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">Features</h2>
          <Button
            ref={newFlagRef}
            variant="primary"
            onClick={(event) => {
              returnFocusRef.current = event.currentTarget;
              setNotice(null);
              setModal({ kind: 'create' });
            }}
            title="Create a feature flag: name it and list the app users who get the feature."
          >
            New Flag
          </Button>
        </div>

        <div className="bg-blue-50 border border-status-info-border text-blue-900 p-3 rounded-md text-sm mb-4">
          Feature flags turn MyFluxHive app features on per app user. Saving a
          change rewrites the flag list stored on each affected app user's
          record; the app applies it on its next sync. Find app user IDs on
          the <Link to="/app-users" className="underline">App Users</Link> page.
        </div>

        {notice && (
          <div
            className={`${
              notice.kind === 'warning'
                ? 'bg-amber-50 border-status-warning-border text-amber-800'
                : 'bg-green-50 border-status-success-border text-green-800'
            } border p-3 rounded-md text-sm mb-4 flex items-start gap-2`}
          >
            <div className="flex-1">{notice.text}</div>
            <Button
              variant="icon"
              onClick={() => setNotice(null)}
              aria-label="Dismiss"
              title="Hide this message."
            >
              &times;
            </Button>
          </div>
        )}

        {loading && <p className="text-muted-foreground">Loading…</p>}
        {error && (
          <div className="border border-status-error-border bg-status-error text-status-error-foreground p-3 rounded-md mb-4">{error}</div>
        )}

        {!loading && !error && flags.length === 0 && (
          <p className="text-muted-foreground text-sm">No feature flags yet.</p>
        )}

        {!loading && !error && flags.length > 0 && (
          <div className="overflow-x-auto">
            <table className="table-auto w-full text-left border-collapse border border-border text-sm">
              <thead>
                <tr className="bg-gray-200">
                  <th className="px-3 py-2 border">Flag</th>
                  <th className="px-3 py-2 border">Enabled App Users</th>
                  <th className="px-3 py-2 border">Updated</th>
                  <th className="px-3 py-2 border">Actions</th>
                </tr>
              </thead>
              <tbody>
                {flags.map((f) => (
                  <tr key={f.name} className="hover:bg-gray-50">
                    <td className="px-3 py-2 border font-mono text-xs">{f.name}</td>
                    <td className="px-3 py-2 border">
                      {f.condition.user_ids.length === 0 ? (
                        <span className="text-xs text-gray-600">(none — off for everyone)</span>
                      ) : (
                        <div className="flex flex-wrap gap-1 items-center">
                          <span className="inline-block border border-status-neutral-border bg-status-neutral text-status-neutral-foreground text-xs px-2 py-0.5 rounded-sm">
                            {f.condition.user_ids.length}
                          </span>
                          {f.condition.user_ids.map((uid) => (
                            <span
                              key={uid}
                              className="inline-block bg-emerald-100 text-emerald-800 text-xs px-2 py-0.5 rounded-sm font-mono"
                            >
                              {uid}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 border text-xs text-gray-600">
                      {f.updated_at ? new Date(f.updated_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-3 py-2 border">
                      <div className="flex gap-1">
                        {f.name === CHART_FLAG_NAME && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={(event) => {
                              returnFocusRef.current = event.currentTarget;
                              setNotice(null);
                              setModal({ kind: 'access', flag: f });
                            }}
                            title="Check and save which devices these users can open charts for"
                          >
                            Device Access
                          </Button>
                        )}
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={(event) => {
                            returnFocusRef.current = event.currentTarget;
                            setNotice(null);
                            setModal({ kind: 'edit', flag: f });
                          }}
                          title="Change which app users have this feature."
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={(event) => {
                            returnFocusRef.current = event.currentTarget;
                            setNotice(null);
                            setModal({ kind: 'delete', flag: f });
                          }}
                          title="Delete this flag. You confirm first; the feature then turns off for its users on their next app sync."
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal.kind === 'create' && (
        <FlagFormModal
          mode="create"
          existingNames={flags.map((f) => f.name)}
          onClose={closeModal}
          onSaved={handleSaved}
        />
      )}
      {modal.kind === 'edit' && (
        <FlagFormModal
          mode="edit"
          flag={modal.flag}
          existingNames={flags.map((f) => f.name)}
          onClose={closeModal}
          onSaved={handleSaved}
        />
      )}
      {modal.kind === 'delete' && (
        <DeleteFlagModal flag={modal.flag} onClose={closeModal} onDeleted={notice => handleSaved(notice, true)} />
      )}
      {modal.kind === 'access' && (
        <ChartAccessModal flag={modal.flag} onClose={closeModal} onSaved={handleSaved} />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Create/Edit modal
// ---------------------------------------------------------------------------

type FlagFormModalProps = {
  mode: 'create';
  flag?: never;
  existingNames: string[];
  onClose: () => void;
  onSaved: (notice: Notice) => void;
} | {
  mode: 'edit';
  flag: FeatureFlag;
  existingNames: string[];
  onClose: () => void;
  onSaved: (notice: Notice) => void;
};

const FlagFormModal: React.FC<FlagFormModalProps> = ({
  mode,
  flag,
  existingNames,
  onClose,
  onSaved,
}) => {
  const [name, setName] = useState(flag?.name ?? '');
  const [idsInput, setIdsInput] = useState(flag?.condition.user_ids.join(', ') ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsedIds = parseUserIdsInput(idsInput);
  const unchanged = mode === 'edit' && name === flag?.name &&
    [...parsedIds].sort().join(',') === [...(flag?.condition.user_ids ?? [])].sort().join(',');

  const nameError =
    name === ''
      ? null // don't nag before anything is typed; submit still validates
      : !FLAG_NAME_REGEX.test(name)
        ? 'Lowercase letters, digits and underscores, starting with a letter (e.g. in_app_charts).'
        : name !== flag?.name && existingNames.includes(name)
          ? 'A flag with this name already exists.'
          : null;
  const nameSubmitError = error?.startsWith('Flag name') || error === 'A flag with this name already exists.';

  const handleSubmit = async () => {
    setError(null);
    if (!FLAG_NAME_REGEX.test(name)) {
      setError(name === ''
        ? 'Flag name is required: enter lowercase letters, digits and underscores, starting with a letter.'
        : 'Flag name must use lowercase letters, digits and underscores, starting with a letter.');
      return;
    }
    if (name !== flag?.name && existingNames.includes(name)) {
      setError('A flag with this name already exists.');
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        name,
        condition: { type: 'user_ids' as const, user_ids: parsedIds },
      };
      const res =
        mode === 'create'
          ? await axiosInstance.post<FlagMutationResponse>(flagsUrl(), body)
          : await axiosInstance.put<FlagMutationResponse>(
              `${flagsUrl()}/${encodeURIComponent(flag.name)}`,
              body
            );
      onSaved(savedMessage('saved', name, res.data ?? {}));
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to save flag'));
      setSubmitting(false);
    }
  };

  const close = () => {
    if (!submitting) onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-2xl" showCloseButton={false}>
        <DialogHeader className="relative shrink-0 border-b pb-3 pr-8">
          <DialogTitle>
            {mode === 'create' ? 'New Flag' : `Edit ${flag?.name ?? ''}`}
          </DialogTitle>
          <DialogDescription>Name the flag and list the app users who receive it on their next sync.</DialogDescription>
          <Button
            variant="icon"
            onClick={close}
            disabled={submitting}
            className="absolute right-0 top-0 text-2xl"
            aria-label="Close"
            title="Close this form without saving."
          >
            &times;
          </Button>
        </DialogHeader>

        {error && <div id="flag-form-error" className="shrink-0 border border-status-error-border bg-status-error text-status-error-foreground p-3 rounded-md text-sm" role="alert">{error}</div>}
        <div className="min-h-0 overflow-y-auto p-2 space-y-4">

          <div>
            <Label htmlFor="flag-name" className="mb-1">Name</Label>
            <Input
              id="flag-name"
              aria-describedby={`flag-name-help${nameError ? ' flag-name-error' : ''}${nameSubmitError ? ' flag-form-error' : ''}`}
              aria-invalid={!!nameError || nameSubmitError || undefined}
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              autoFocus={mode === 'create'}
              spellCheck={false}
              placeholder="in_app_charts"
              className="w-full font-mono text-sm"
            />
            <p id="flag-name-help" className="text-xs text-muted-foreground mt-1">The app checks this exact name; renaming can turn off a released feature.</p>
            {nameError ? (
              <p id="flag-name-error" className="text-xs text-red-700 mt-1">{nameError}</p>
            ) : mode === 'edit' && name !== flag?.name ? (
              <p className="text-xs text-amber-700 mt-1">
                Renaming removes "{flag?.name}" from app user records — app versions
                checking the old name lose the feature.
              </p>
            ) : null}
          </div>

          <div>
            <Label htmlFor="flag-user-ids" className="mb-1">Condition</Label>
            <p className="text-sm text-gray-700 mb-2">User IDs — on for the listed app users</p>
            <Textarea
              id="flag-user-ids"
              aria-describedby="flag-user-ids-help flag-user-ids-count"
              value={idsInput}
              onChange={(e) => {
                setIdsInput(e.target.value);
                setError(null);
              }}
              rows={4}
              spellCheck={false}
              placeholder="comma-separated Firestore user IDs"
              className="w-full font-mono text-sm"
            />
            <p id="flag-user-ids-help" className="text-xs text-muted-foreground mt-1">Enter comma separated Firestore user IDs. An empty list turns the flag off for everyone.</p>
            <p id="flag-user-ids-count" className="text-xs text-muted-foreground mt-1">
              {parsedIds.length === 0
                ? 'No user IDs — the flag is off for everyone.'
                : `${parsedIds.length} user ID${parsedIds.length === 1 ? '' : 's'}.`}
            </p>
          </div>
        </div>

        <DialogFooter className="shrink-0 flex-wrap">
          <ActionRow
            align="end"
            className="flex-1"
            reason={unchanged && 'No changes to save.'}
            reasonId="flag-save-reason"
          >
            <Button
              variant="secondary"
              onClick={close}
              disabled={submitting}
              title="Close this form without saving."
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting || unchanged}
              aria-describedby={unchanged ? 'flag-save-reason' : undefined}
              title="Save this flag. The listed app users get the feature on their next app sync."
            >
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </ActionRow>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Delete confirmation modal
// ---------------------------------------------------------------------------

const DeleteFlagModal: React.FC<{
  flag: FeatureFlag;
  onClose: () => void;
  onDeleted: (notice: Notice) => void;
}> = ({ flag, onClose, onDeleted }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enabledCount = flag.condition.user_ids.length;

  const handleDelete = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await axiosInstance.delete<FlagMutationResponse>(
        `${flagsUrl()}/${encodeURIComponent(flag.name)}`
      );
      onDeleted(savedMessage('deleted', flag.name, res.data ?? {}));
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to delete flag'));
      setSubmitting(false);
    }
  };

  const close = () => {
    if (!submitting) onClose();
  };

  return (
    <AlertDialog open onOpenChange={(open) => { if (!open) close(); }}>
      <AlertDialogContent className="sm:max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Flag</AlertDialogTitle>
          <AlertDialogDescription>
            Delete <strong className="font-mono">{flag.name}</strong>?
          </AlertDialogDescription>
          <p className="text-sm text-muted-foreground mb-4">
            {enabledCount === 0
              ? 'No app users currently have this flag.'
              : `The feature turns off for ${enabledCount} app user${
                  enabledCount === 1 ? '' : 's'
                } on their next sync.`}
          </p>
          {error && (
            <div role="alert" className="border border-status-error-border bg-status-error text-status-error-foreground p-3 rounded-md text-sm mb-3">{error}</div>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button
            variant="secondary"
            onClick={close}
            disabled={submitting}
            title="Close this dialog and keep the flag."
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={submitting}
            title="Delete this flag now. The feature turns off for its app users on their next sync."
          >
            {submitting ? 'Deleting…' : 'Delete'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// ---------------------------------------------------------------------------
// Device access review — the chart flag's second half
// ---------------------------------------------------------------------------

interface ChartAccessModalProps {
  flag: FeatureFlag;
  onClose: () => void;
  onSaved: (notice: Notice) => void;
}

const DeviceChangeChip: React.FC<{ change: PlanDevice['change'] }> = ({ change }) =>
  change === 'grant' ? (
    <span className="inline-block bg-emerald-100 text-emerald-800 text-xs px-2 py-0.5 rounded-sm">
      To add
    </span>
  ) : (
    <span className="inline-block border border-status-neutral-border bg-status-neutral text-status-neutral-foreground text-xs px-2 py-0.5 rounded-sm">
      Already allowed
    </span>
  );

/**
 * Shows what the server proposes for each enabled app user, then saves exactly
 * what is left ticked. Two steps on purpose: the proposal is built from the
 * devices users registered themselves, so an admin confirms it before it
 * becomes permission. Unticking a row that is already allowed removes it.
 */
const ChartAccessModal: React.FC<ChartAccessModalProps> = ({ flag, onClose, onSaved }) => {
  const [plan, setPlan] = useState<ChartAccessPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const loadPlan = useCallback(async () => {
    const res = await axiosInstance.get<ChartAccessPlan>(`${chartAccessUrl(flag.name)}/plan`);
    setPlan(res.data);
    setExcluded(new Set());
  }, [flag.name]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadPlan();
      } catch (err) {
        if (!cancelled) setError(extractErrorMessage(err, 'Failed to check device access'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadPlan]);

  const toggleDevice = (uid: string, serial: string) => {
    const key = deviceKey(uid, serial);
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const summary = plan ? selectionSummary(plan, excluded) : null;

  const handleSave = async () => {
    if (!plan) return;
    setSaving(true);
    setError(null);
    try {
      const res = await axiosInstance.post<ApplyReceipt>(`${chartAccessUrl(flag.name)}/apply`, {
        generated_at: plan.generated_at,
        access: buildAccessMap(plan, excluded),
        baseline: plan.baseline,
      });
      onSaved({ kind: 'success', text: receiptText(res.data ?? {}) });
    } catch (err) {
      // 409: someone else changed device access while this was open. The
      // server wrote nothing; reload so the admin reviews the current state.
      const overtaken = axios.isAxiosError(err) && err.response?.status === 409;
      setError(extractErrorMessage(err, 'Failed to save device access'));
      setSaving(false);
      if (overtaken) {
        try {
          await loadPlan();
        } catch (reloadError) {
          setError(`${extractErrorMessage(err, 'Failed to save device access')} ${extractErrorMessage(reloadError, 'Failed to reload the current access plan')}`);
        }
      }
    }
  };

  const close = () => {
    if (!saving) onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-3xl" showCloseButton={false}>
        <DialogHeader className="relative shrink-0 border-b pb-3 pr-8">
          <DialogTitle>Device Access</DialogTitle>
          <DialogDescription>Review and save which devices each enabled app user can open charts for.</DialogDescription>
          <Button
            variant="icon"
            onClick={close}
            disabled={saving}
            className="absolute right-0 top-0 text-2xl"
            aria-label="Close"
            title="Close without saving any device access changes."
          >
            &times;
          </Button>
        </DialogHeader>

        {error && <div role="alert" className="shrink-0 border border-status-error-border bg-status-error text-status-error-foreground p-3 rounded-md text-sm">{error}</div>}
        <div className="min-h-0 overflow-y-auto flex-1 p-2">
          <p className="text-sm text-gray-600 mb-4">
            Turning this feature on for an app user is only half of it — charts open
            only for devices listed here. Review what each user should see, then save.
          </p>
          <p id="chart-access-help" className="text-xs text-muted-foreground mb-3">Tick a device to allow charts for that app user; untick it to remove access.</p>

          {loading && <p className="text-muted-foreground text-sm">Checking device access…</p>}

          {plan && (
            <>
              <div className="bg-blue-50 border border-status-info-border text-blue-900 p-3 rounded-md text-sm mb-4">
                {summary && summary.changes === 0
                  ? 'Everything is already up to date — nothing to save.'
                  : `${summary?.grants ?? 0} device${summary?.grants === 1 ? '' : 's'} to add, ` +
                    `${summary?.revocations ?? 0} to remove, across ${plan.summary.users} app user` +
                    `${plan.summary.users === 1 ? '' : 's'}.`}
              </div>

              {plan.users.map((user) => (
                <div key={user.uid} className="border border-border rounded-md mb-3">
                  <div className="flex items-baseline justify-between px-3 py-2 bg-gray-50 border-b">
                    <div className="font-medium text-sm">
                      {userLabel(user)}
                      {user.email && user.name && <span className="text-muted-foreground font-normal ml-2">{user.name}</span>}
                    </div>
                    <div className="font-mono text-xs text-muted-foreground">{user.uid}</div>
                  </div>
                  <div className="p-3">
                    {user.warnings.map((code) => (
                      <div
                        key={code}
                        className="bg-amber-50 border border-status-warning-border text-amber-800 text-xs p-2 rounded-sm mb-2"
                      >
                        {warningText[code] ?? code}
                      </div>
                    ))}

                    {user.devices.length === 0 && user.revoked.length === 0 && (
                      <p className="text-xs text-muted-foreground">No devices registered to this user.</p>
                    )}

                    {user.devices.map((device) => (
                      <label
                        key={device.serial}
                        className="flex items-center gap-2 py-1 text-sm cursor-pointer"
                      >
                        <Checkbox
                          checked={!excluded.has(deviceKey(user.uid, device.serial))}
                          onCheckedChange={() => toggleDevice(user.uid, device.serial)}
                          aria-describedby="chart-access-help"
                        />
                        <span className="font-mono text-xs">{device.serial}</span>
                        <span className="text-gray-600 text-xs">
                          {device.known_device
                            ? `Device ${device.device_id}${device.description ? ` — ${device.description}` : ''}`
                            : 'Not in the fleet records'}
                        </span>
                        <DeviceChangeChip change={device.change} />
                        {device.claimed_by.length > 1 && (
                          <span className="inline-block border border-status-warning-border bg-status-warning text-status-warning-foreground text-xs px-2 py-0.5 rounded-sm">
                            Registered to {device.claimed_by.length} users
                          </span>
                        )}
                      </label>
                    ))}

                    {user.revoked.map((serial) => (
                      <div key={serial} className="flex items-center gap-2 py-1 text-sm">
                        <span className="font-mono text-xs line-through text-muted-foreground">{serial}</span>
                        <span className="inline-block border border-status-error-border bg-status-error text-status-error-foreground text-xs px-2 py-0.5 rounded-sm">
                          To remove — no longer registered to this user
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {plan.stale_mappings.length > 0 && (
                <div className="border border-status-error-border rounded-md p-3">
                  <div className="text-sm font-medium text-red-800 mb-1">
                    Access to remove — these app users no longer have the feature
                  </div>
                  {plan.stale_mappings.map((stale) => (
                    <div key={stale.uid} className="text-xs text-gray-600 py-0.5">
                      <span className="font-mono">{stale.email ?? stale.uid}</span>
                      {stale.devices.length > 0 && ` — ${stale.devices.join(', ')}`}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="shrink-0 flex-wrap">
          <ActionRow
            align="end"
            className="flex-1"
            reason={plan && summary?.changes === 0 && 'No changes to save.'}
            reasonId="chart-access-save-reason"
          >
            <Button
              variant="secondary"
              onClick={close}
              disabled={saving}
              title="Close without saving any device access changes."
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving || !plan || (summary?.changes ?? 0) === 0}
              aria-describedby={plan && summary?.changes === 0 ? 'chart-access-save-reason' : undefined}
              title="Save the device list above: each user can open charts only for the devices ticked for them."
            >
              {saving ? 'Saving…' : 'Save Device Access'}
            </Button>
          </ActionRow>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FeaturesPage;
